import { ActorRefFrom, createActor, waitFor } from 'xstate';

import { EluveError, EluveErrorArgs } from '@eluve/eluve-errors';
import { Logger } from '@eluve/logger';

import { JWT_REFRESH_LOCK_NAME, withRefreshLock } from './refresh-lock';
import { sessionMachine } from './session.machine';
import { AccessTokenProvider, ISessionProvider, SessionMachine } from './types';

const TOKEN_TIMEOUT_MS = 20_000;

export type TokenAcquisitionErrorReason =
  | 'tenant-switch-failed'
  | 'token-timeout';

type TokenAcquisitionErrorArgs = EluveErrorArgs & {
  reason?: TokenAcquisitionErrorReason;
};

/**
 * Error thrown when token acquisition fails after exhausting all retries.
 * This allows callers to handle acquisition failures gracefully instead of
 * timing out after 20 seconds.
 */
export class TokenAcquisitionError extends EluveError {
  static override readonly errorName = 'TokenAcquisitionError';
  readonly logLevel = 'error' as const;

  constructor(
    message = 'Failed to acquire access token',
    args?: TokenAcquisitionErrorArgs,
  ) {
    const metadata = args?.reason
      ? { ...args.metadata, reason: args.reason }
      : args?.metadata;

    super(message, { ...args, metadata });
  }
}

export const sessionAccessTokenProvider: (args: {
  sessionProvider: ISessionProvider;
  logger?: Logger;
}) => {
  tokenProvider: AccessTokenProvider;
  sessionActor: ActorRefFrom<SessionMachine>;
} = (args) => {
  const { sessionProvider, logger } = args;

  const sessionActor = createActor(sessionMachine, {
    input: {
      logger,
      sessionProvider,
    },
  });

  sessionActor.start();

  /**
   * Tracks the current in-flight token acquisition promise.
   * This enables promise deduplication - concurrent requests share the same
   * acquisition instead of each waiting independently.
   *
   * This is critical for batch operations (e.g., signing multiple notes)
   * where many GraphQL requests fire concurrently.
   */
  let pendingAcquisition: Promise<{ jwt: string; role: string }> | null = null;

  /**
   * Tracks whether we're currently in a hard refresh flow.
   * Used to determine if we should reuse the pending promise or start fresh.
   */
  let isHardRefreshInProgress = false;

  /**
   * Generation counter for in-flight acquisitions. Each new IIFE bumps it; the
   * IIFE's finally only clears module state if its generation still matches —
   * preventing a settling soft IIFE from clobbering a later hard IIFE that
   * replaced pendingAcquisition.
   */
  let acquisitionGeneration = 0;

  // Drive the actor from Idle (or current state) to TokenAvailable.
  // Throws TokenAcquisitionError if the actor returns to Idle after starting
  // acquisition or transitions to TenantSwitchFailed.
  const acquire = async (): Promise<{ jwt: string; role: string }> => {
    let hasLeftIdle = false;

    const snapshot = sessionActor.getSnapshot();
    if (snapshot.matches('Idle')) {
      sessionActor.send({ type: 'ACQUIRE_TOKEN' });
    } else {
      hasLeftIdle = true;
    }

    const finalState = await waitFor(
      sessionActor,
      (state) => {
        if (state.matches('TokenAvailable')) return true;
        if (state.matches('TenantSwitchFailed')) return true;
        if (!state.matches('Idle')) hasLeftIdle = true;
        // Returning to Idle after we've left it means acquisition failed.
        if (hasLeftIdle && state.matches('Idle')) return true;
        return false;
      },
      { timeout: TOKEN_TIMEOUT_MS },
    );

    if (finalState.matches('TenantSwitchFailed')) {
      throw new TokenAcquisitionError(undefined, {
        reason: 'tenant-switch-failed',
      });
    }

    if (finalState.matches('Idle')) {
      // Acquisition exhausted its retries / the actor fell back to Idle. This
      // is a transient auth-acquisition failure (usually upstream SuperTokens
      // instability), not a genuine render bug — tag it so the error boundary
      // can offer a recoverable reconnect path instead of a full-page crash.
      throw new TokenAcquisitionError(undefined, { reason: 'token-timeout' });
    }

    return {
      jwt: finalState.context.jwt,
      role: finalState.context.defaultRole,
    };
  };

  return {
    sessionActor,
    tokenProvider: async (options = { hardRefresh: false }) => {
      const { hardRefresh } = options;

      // Share the in-flight promise when (a) a hard refresh is already running
      // (any caller can ride along) or (b) the caller wants a soft refresh.
      // SuperTokens rotates the refresh token on each call, so concurrent hard
      // refreshes lose the race and 401 the losers.
      if (pendingAcquisition && (isHardRefreshInProgress || !hardRefresh)) {
        return pendingAcquisition;
      }

      // Set synchronously so a same-tick second caller sees the flag and
      // shares pendingAcquisition rather than launching a parallel refresh.
      if (hardRefresh) {
        isHardRefreshInProgress = true;
      }

      // Generation-check in finally so a soft IIFE that completes after a
      // later hard IIFE replaced pendingAcquisition does not clobber the hard
      // refresh's slot. Without this, new callers arriving after the soft
      // settles but before the hard does would bypass dedup and launch a
      // parallel acquisition.
      const generation = ++acquisitionGeneration;
      pendingAcquisition = (async () => {
        try {
          if (hardRefresh) {
            logger?.info('Hard refresh of access token requested');
            // Cross-tab serialization: hold the lock across the full acquire
            // cycle. SuperTokens' SDK will skip the network call when a queued
            // tab eventually runs and finds the cookie already fresh.
            return await withRefreshLock(JWT_REFRESH_LOCK_NAME, async () => {
              sessionActor.send({ type: 'HARD_REFRESH_REQUESTED' });
              return acquire();
            });
          }
          return await acquire();
        } finally {
          if (acquisitionGeneration === generation) {
            pendingAcquisition = null;
            isHardRefreshInProgress = false;
          }
        }
      })();

      return pendingAcquisition;
    },
  };
};
